import type { ReactNode } from 'react';
import { Alert, Box, Chip, Paper, Stack, Typography } from '@mui/material';

import type { BillingCatalogDto, CompanyBillingSummaryDto } from '@shared/services/apiService';

import { billingDate, formatCreditUnits } from '../model/formatBillingUnits';
import { BillingCreditBalance } from './BillingCreditBalance';

const policyLabels: Record<string, string> = {
  'POL-01': 'Trial and plan rules',
  'POL-02': 'Charging and profile rules',
  'POL-03': 'Origin-period overrun policy',
  'POL-04': 'Payment exceptions, grace and retention rules',
  'POL-05': 'Cohort, permissions and payment-management rollout rules',
};

export const billingStatusLabels: Record<CompanyBillingSummaryDto['status'], string> = {
  legacy: 'Existing access',
  trial_pending: 'Trial awaiting activation',
  trial: 'Trial',
  paid: 'Paid plan',
  expired: 'Credit period expired',
  review: 'Needs attention',
};

export const BillingSummary = ({
  summary,
  catalog,
  showPlanDetails = true,
  renderCredits,
}: {
  summary: CompanyBillingSummaryDto;
  catalog?: BillingCatalogDto;
  showPlanDetails?: boolean;
  renderCredits?: (availableUnits: string) => ReactNode;
}) => {
  const previous = !summary.plan && summary.previousPeriod;
  const plan = summary.plan ?? (previous ? previous.plan : null);
  const balance = summary.balance ?? (previous ? previous.balance : null);
  const scale = plan?.creditScale ?? 0;
  const available =
    summary.totalAvailableUnits ??
    (summary.admissionEnabled && ['trial', 'paid'].includes(summary.status)
      ? (summary.balance?.availableUnits ?? '0')
      : '0');
  const exhausted = available === '0';
  return (
    <Stack spacing={2}>
      {renderCredits
        ? renderCredits(available)
        : (showPlanDetails || balance) && (
            <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 3 }}>
              {showPlanDetails && (
                <>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={2}>
                    <Box>
                      <Typography variant="overline">
                        {previous ? 'Previous period' : 'Current access'}
                      </Typography>
                      <Typography variant="h5" fontWeight={600} sx={{ overflowWrap: 'anywhere' }}>
                        {plan?.key ?? billingStatusLabels[summary.status]}
                      </Typography>
                    </Box>
                    <Chip
                      label={billingStatusLabels[summary.status]}
                      color={summary.status === 'review' ? 'warning' : 'default'}
                      sx={(theme) => {
                        const state =
                          theme.palette.status[summary.status === 'review' ? 'warning' : 'neutral'];
                        return {
                          color: state.foreground,
                          bgcolor: state.background,
                          border: '1px solid',
                          borderColor: state.border,
                        };
                      }}
                    />
                  </Stack>
                  {summary.status === 'legacy' ? (
                    <Typography color="text.secondary" sx={{ mt: 2 }}>
                      Your company keeps its existing access and limits. Opening Billing does not
                      enroll it in a subscription or start a trial.
                    </Typography>
                  ) : (
                    <>
                      <Typography color="text.secondary" sx={{ mt: 1 }}>
                        {previous ? 'Period ended' : 'Period ends'}:{' '}
                        {billingDate(summary.endsAt ?? (previous ? previous.endsAt : null))}
                      </Typography>
                      {summary.plan && (
                        <Typography color="text.secondary">
                          Simultaneous calls: {summary.plan.concurrentLimit}. Your company shares
                          this capacity.
                        </Typography>
                      )}
                    </>
                  )}
                </>
              )}
              {balance && (
                <Box sx={{ mt: showPlanDetails ? 3 : 0 }}>
                  <BillingCreditBalance summary={summary} availableUnits={available} />
                </Box>
              )}
            </Paper>
          )}
      {balance?.overrunUnits !== '0' && balance && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          Recorded overrun: {formatCreditUnits(balance.overrunUnits, scale)} credits. This stays in
          its original period and will not reduce a later package or create an extra charge.
        </Alert>
      )}
      {summary.mode === 'credit_managed' && !summary.admissionEnabled && (
        <Alert severity="info">New calls are paused. Existing active calls can finish.</Alert>
      )}
      {exhausted && (
        <Alert severity="warning">
          No credits are currently available for new calls. Active calls can finish; reserved
          obligations remain accounted for.
        </Alert>
      )}
      {summary.policyGates.length > 0 && (
        <Alert severity="warning">
          Activation is waiting for product-policy approval:{' '}
          {summary.policyGates
            .map((code) => policyLabels[code] ?? 'Billing launch policy')
            .join(', ')}
          .
        </Alert>
      )}
      {summary.status === 'review' && (
        <Alert severity="warning">
          Billing needs operator attention. Synchronize the account or contact your billing
          administrator.
        </Alert>
      )}
      {catalog?.pendingChange && (
        <Alert severity="info">
          {catalog.pendingChange.kind === 'checkout' ? 'Subscription activation' : 'Plan change'} is
          pending server confirmation. Current credits and access remain authoritative.
          {catalog.pendingChange.nextPlan && (
            <>
              {' '}
              Requested plan: {catalog.pendingChange.nextPlan.key};{' '}
              {formatCreditUnits(
                catalog.pendingChange.nextPlan.allowanceUnits,
                catalog.pendingChange.nextPlan.creditScale,
              )}{' '}
              credits and {catalog.pendingChange.nextPlan.concurrentLimit} simultaneous calls after
              verified activation.
            </>
          )}
          {catalog.pendingChange.effectiveAt && (
            <> Requested boundary: {billingDate(catalog.pendingChange.effectiveAt)}.</>
          )}
        </Alert>
      )}
      {catalog?.scheduledChange && (
        <Alert severity="info">
          {catalog.scheduledChange.kind === 'cancel'
            ? 'Cancellation'
            : `Change to ${catalog.scheduledChange.planKey ?? 'the next plan'}`}{' '}
          scheduled for {billingDate(catalog.scheduledChange.effectiveAt)}. The current plan remains
          in effect until its boundary.
        </Alert>
      )}
      {summary.outstandingPeriods.length > 0 && (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Stack spacing={2}>
            <Typography variant="h6">Period holds and recorded overruns</Typography>
            <Typography color="text.secondary">
              Reserved credits and recorded overruns remain attached to their original service
              period. Recorded overrun does not reduce later packages or create extra charges.
            </Typography>
            {summary.outstandingPeriods.map((period) => (
              <Box key={period.periodId}>
                <Typography variant="body2">
                  Period ended/ends {billingDate(period.endsAt)}
                </Typography>
                <Typography color="text.secondary">
                  Reserved: {formatCreditUnits(period.heldUnits, period.creditScale)} · Recorded
                  overrun: {formatCreditUnits(period.overrunUnits, period.creditScale)} credits
                </Typography>
              </Box>
            ))}
            {summary.hasMoreOutstandingPeriods && (
              <Typography color="text.secondary">
                Additional period holds and overrun history are available through billing support.
              </Typography>
            )}
          </Stack>
        </Paper>
      )}
    </Stack>
  );
};
